/**
 * A donut with a legend — a faithful port of `CS.charts.donut` from the validated mockups.
 *
 * Same geometry (120 viewBox, r=50, 14 stroke), same `-rotate-90` so the first slice starts
 * at twelve o'clock, same legend with count and percentage.
 *
 * ⚠ Every slice carries a `<title>`, which is what makes a thin slice readable at all: with
 * 0.2% of a board a slice is a couple of pixels wide and the legend is the only other place
 * its number appears.
 */

import { formatNumber } from "@cocon-stats/shared";

export interface DonutSlice {
  label: string;
  n: number;
  color: string;
}

export function Donut({
  slices,
  centreLabel = "Total",
  size = 140,
  onSelect,
  selected,
}: {
  slices: DonutSlice[];
  centreLabel?: string;
  size?: number;
  /** Clicking a legend row filters the screen. Omitted where there is nothing to filter. */
  onSelect?: (label: string) => void;
  selected?: string | null;
}) {
  const total = slices.reduce((sum, s) => sum + s.n, 0) || 1;
  const r = 50;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="flex flex-wrap items-center gap-5">
      <div className="relative shrink-0">
        <svg viewBox="0 0 120 120" style={{ width: size, height: size }} className="-rotate-90">
          <circle cx="60" cy="60" r={r} fill="none" stroke="#f1f5f9" strokeWidth="14" />
          {slices.map((slice) => {
            const len = (slice.n / total) * circumference;
            const dash = `${len} ${circumference - len}`;
            const thisOffset = -offset;
            offset += len;
            return (
              <circle
                key={slice.label}
                cx="60"
                cy="60"
                r={r}
                fill="none"
                stroke={slice.color}
                strokeWidth="14"
                strokeDasharray={dash}
                strokeDashoffset={thisOffset}
                opacity={selected && selected !== slice.label ? 0.25 : 1}
              >
                <title>{`${slice.label}: ${slice.n}`}</title>
              </circle>
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="num text-xl font-bold text-slate-900">{formatNumber(total)}</span>
          <span className="eyebrow">{centreLabel}</span>
        </div>
      </div>

      <div className="min-w-[180px] flex-1 space-y-1.5">
        {slices.map((slice) => {
          const row = (
            <>
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: slice.color }} />
              <span className="flex-1 truncate text-left text-slate-600">{slice.label}</span>
              <span className="num font-bold text-slate-800">{formatNumber(slice.n)}</span>
              <span className="num w-10 text-right text-slate-400">{Math.round((slice.n / total) * 100)}%</span>
            </>
          );
          return onSelect ? (
            <button
              key={slice.label}
              type="button"
              onClick={() => onSelect(slice.label)}
              className={`flex w-full items-center gap-2 rounded px-1 py-0.5 text-xs transition-colors hover:bg-slate-50 ${
                selected === slice.label ? "bg-slate-100 font-semibold" : ""
              }`}
              title={selected === slice.label ? "Click again to clear this filter" : `Show only ${slice.label}`}
            >
              {row}
            </button>
          ) : (
            <div key={slice.label} className="flex items-center gap-2 px-1 text-xs">
              {row}
            </div>
          );
        })}
      </div>
    </div>
  );
}
